import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  ActionBar,
  ActionBarMessage,
  Button,
  Column,
  Columns,
  Header,
  PageHeader,
  Section,
  ToastNotifications,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import { NoPermissionsPage } from '@vend/react-utilities'
import { trackUserEvent } from '@vend/utilities'
import { add, round, subtract } from 'vend-number'
import {
  usePaymentManager,
  useRegisterClosureManager,
  useSaleManager,
} from '../../ng-compat/hooks'
import { NoteInput } from '../shared/NoteInput'
import { $state } from '../../ng-compat/imports'
import { STATES } from '../../../../lib/routes'
import {
  isCashConcealedPaymentType,
  isReadOnlyPaymentType,
} from '../../functions/payments'
import {
  DenominationQuantity,
  RegisterClosure,
} from '../../util/register-closure-types'
import {
  ClosureTotals,
  RegisterClosureSummary,
  RowCount,
} from './RegisterClosureSummary'
import { getCashMovementsTotal, useRegisterClosure } from './useRegisterClosure'
import { NOT_APPLICABLE } from './useFormatClosureNumber'
import { filterPaymentTypes } from './filterPaymentTypes'

const CloseRegisterHeader: FunctionComponent = () => {
  return (
    <>
      <PageHeader>
        <T _str="Close register" />
      </PageHeader>
      <ActionBar variant="page">
        <ActionBarMessage>
          <T _str="Close your register to finalize payments and sales for the day." />
        </ActionBarMessage>
      </ActionBar>
    </>
  )
}

export const CloseRegister: FunctionComponent = () => {
  const t = useT()
  const registerClosureManager = useRegisterClosureManager()
  const saleManager = useSaleManager()
  const paymentManager = usePaymentManager()
  const canAccess = registerClosureManager.hasOpenClosePermission()
  const summary = useRegisterClosure({ type: 'current' })
  const [counts, setCounts] = useState<Record<string, RowCount>>({})
  const [note, setNote] = useState<string>('')
  const [closing, setClosing] = useState<boolean>(false)
  const [totals, setTotals] = useState<ClosureTotals>({
    expectedTotal: NOT_APPLICABLE,
    countedTotal: NOT_APPLICABLE,
    differenceTotal: NOT_APPLICABLE,
    countedValid: true,
  })
  const [ready, setReady] = useState<boolean>(false)
  const [denominationsUsed, setDenominationsUsed] = useState<
    DenominationQuantity[]
  >([])

  const hasError = useMemo(
    () => Object.values(counts || {}).some(count => count.error),
    [counts]
  )

  const handleCloseRegister = async () => {
    if (saleManager.hasActiveSale()) {
      return saleManager.actionExistingSaleIfRequired({
        context: 'CLOSE_REGISTER',
      })
    }

    // If a field hasn't been touched the validators can
    // miss empty fields
    let hasEmptyCount = false
    Object.entries(counts).forEach(([key, value]) => {
      if (value.count === '' || value.count === undefined) {
        setError(key, true)
        hasEmptyCount = true
      }
    })

    if (hasError || hasEmptyCount) {
      ToastNotifications.negative(
        t('Please enter the amount for the highlighted fields')
      )
      return
    }

    const currentSummary = applyCountsToSummary(counts)

    setClosing(true)
    try {
      await registerClosureManager.closeRegister(currentSummary)
      registerClosureManager.saveRegisterClosure(currentSummary)
      if (denominationsUsed && denominationsUsed.length) {
        trackUserEvent('Cash Counting', 'Register Closed', {
          denominationsUsed,
        })
      }
      $state.go(STATES.RESOLVE_REGISTER_CLOSURE_STATE)
    } catch (error) {
      setClosing(false)
    }
  }

  const applyCountsToSummary = useCallback(
    (currentCounts: Record<string, RowCount>) => {
      if (!summary) {
        return
      }
      const newSummary = { ...summary }

      const newPayments = newSummary.payments.map(payment => {
        if (isReadOnlyPaymentType(payment)) {
          payment.totalCounted = payment.total || ''
          return payment
        }
        const count = currentCounts[payment.payment_type_id]
        payment.totalCounted = count?.count || ''

        if (count?.isCashManagement) {
          // Awkward but necessary side effect that at least is local
          // to the applyCountsToSummary function.
          if (payment.totalCounted !== '') {
            const totalMovements = getCashMovementsTotal(newSummary) || 0
            payment.totalCounted = round(
              subtract(payment.totalCounted, totalMovements),
              2
            )
          }

          newSummary.countedCashInDrawer = count.count || ''
        }
        return payment
      })

      newSummary.payments = newPayments
      if (note || newSummary.note) {
        newSummary.note = note
      }

      return newSummary
    },
    [note, summary]
  )

  const calculateTotalsForSummary = useCallback(
    (summary: RegisterClosure | null) => {
      if (!summary) {
        return {
          expectedTotal: NOT_APPLICABLE,
          countedTotal: NOT_APPLICABLE,
          differenceTotal: NOT_APPLICABLE,
          countedValid: true,
        }
      }

      const filteredSummary = {
        ...summary,
        payments: filterPaymentTypes(summary.payments, summary.outlet.id),
      }

      return registerClosureManager.determineSummaryTotals(
        filteredSummary,
        filteredSummary.payments.some(isCashConcealedPaymentType)
      )
    },
    [registerClosureManager]
  )

  useEffect(() => {
    const createCounts = () => {
      if (!summary) {
        return
      }
      if (!ready) {
        setReady(true)
      }
      const newCounts: Record<string, RowCount> = { ...counts }
      let hasChanges = false
      const filteredPayments = filterPaymentTypes(
        summary.payments,
        summary.outlet.id
      )
      filteredPayments.forEach(payment => {
        if (newCounts[payment.payment_type_id]) {
          return
        }

        if (paymentManager.isCashManagementPaymentType(payment)) {
          const totalMovements = getCashMovementsTotal(summary) || 0
          const totalCounted = payment.totalCounted
          const count =
            totalCounted === '' || totalCounted === undefined
              ? ''
              : round(add(totalMovements, totalCounted), 2)
          newCounts[payment.payment_type_id] = {
            isCashManagement: true,
            count,
            error: false,
          }
          hasChanges = true
        } else if (!isReadOnlyPaymentType(payment)) {
          newCounts[payment.payment_type_id] = {
            isCashManagement: false,
            count: payment.totalCounted || '0.00',
            error: false,
          }
          hasChanges = true
        }
        if (hasChanges) {
          setCounts(newCounts)

          const totals = calculateTotalsForSummary(
            applyCountsToSummary(newCounts) || null
          )
          setTotals(totals)
        }
      })
    }

    createCounts()
  }, [
    applyCountsToSummary,
    calculateTotalsForSummary,
    counts,
    paymentManager,
    ready,
    summary,
  ])

  const setError = useCallback(
    (key: string, error: boolean) => {
      const newCounts = {
        ...counts,
        [key]: { ...counts[key], error },
      }
      setCounts(newCounts)
    },
    [counts]
  )

  const setCount = useCallback(
    (paymentTypeId: string, count: string) => {
      const newCounts = {
        ...counts,
        [paymentTypeId]: { ...counts[paymentTypeId], count },
      }
      setCounts(newCounts)

      const currentSummary = applyCountsToSummary(newCounts)
      const totals = calculateTotalsForSummary(currentSummary || null)
      setTotals(totals)
      registerClosureManager.saveRegisterClosure(currentSummary)
    },
    [
      applyCountsToSummary,
      calculateTotalsForSummary,
      counts,
      registerClosureManager,
    ]
  )

  if (!canAccess) {
    return (
      <>
        <CloseRegisterHeader />
        <Section className="vd-pt10">
          <NoPermissionsPage
            copy={t(
              'You don’t have permission to close the register. Switch to a user with permission.',
              { _context: 'Permission error page' }
            )}
            promptToSwitchUser={true}
            backgroundColor="transparent"
          />
        </Section>
      </>
    )
  }

  if (!ready) {
    return null
  }

  return (
    <>
      <CloseRegisterHeader />
      <RegisterClosureSummary
        type="current"
        counts={counts}
        setCount={setCount}
        totals={totals}
        setError={setError}
        onDenominationChanged={denominationsUsed => {
          setDenominationsUsed(denominationsUsed)
        }}
      />
      <hr className="vd-hr" />
      <Section>
        <Columns space={10} collapseBelow="tablet">
          <Column width="3/12">
            <Header level="2" type="sub-heading" className="vd-mb5">
              <T _str="Closing Summary" />
            </Header>
          </Column>
          <Column width="9/12">
            <div className="vd-field">
              <div className="vd-label">
                <T _str="Note" />
              </div>
              <div className="vd-value">
                <NoteInput
                  note={note}
                  onChange={e => {
                    setNote(e.target.value)
                  }}
                  charLimit={2000}
                  placeholder={t('Type to add a register closure note')}
                />
              </div>
            </div>
            <Button
              onClick={handleCloseRegister}
              disabled={closing}
              variant="go"
              jumbo
              className="vd-mt10 wr-closure-summary-form-close-register-button"
            >
              <T _str="Close register" />
            </Button>
          </Column>
        </Columns>
      </Section>
    </>
  )
}
